import { ReactElement } from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { AxisConfig, DataSeriesUnit } from "src/components/monitoring/commonTypes";
import { ChartGrid } from "src/components/monitoring/axisLegends/ChartGrid";
import { YAxisLegend } from "src/components/monitoring/axisLegends/YAxisLegend";
import { getLabelsForYAxis } from "src/components/monitoring/axisUtils";
import { Bar } from "@nivo/bar";
import { SenecBaseTheme } from "src/newsenec-ui-components";
import { senecColors } from "src/styles/senecBaseThemePalette";

/**
 * Represents a single bar item in a bar chart.
 */
export type BarChartDataItem = {
    /** Value of an item */
    value: number;
    /** Label to be displayed */
    label?: string;
};

export type BarChartProps = {
    /** Array of values bar chart data items */
    barChartData: BarChartDataItem[];

    /** @deprecated Whether to display the left Y-axis.  Use leftAxisConfig for left axis config */
    showLeftAxis?: boolean;

    /** Left axis config */
    leftAxisConfig?: AxisConfig;

    /** Locale setting for the chart, used for formatting numbers. */
    locale: Intl.Locale;

    /** Amount of horizontal lines in the Chart. @default is 6 */
    chartLines?: number;

    /** List of indexes of bars to be highlighted*/
    timeframesToHighlight?: number[];
};

/**
 *  Bar chart component
 *
 *  @param locale for localizing numbers.
 *  @param showLeftAxis whether the left axis is shown.
 *  @param leftAxisConfig config for left axis.
 *  @param barChartData data for bars
 *  @param chartLines amount of horizontal lines in the chart.
 *  @param timeframesToHighlight determines which timeframes should be highlighted
 */
export const SenecBarChart = ({
    barChartData,
    showLeftAxis,
    leftAxisConfig,
    locale,
    chartLines = 6,
    timeframesToHighlight = [],
}: BarChartProps): ReactElement => {
    const theme: SenecBaseTheme = useTheme();

    const maxEnergyYValue = calculateMaxEnergyYValue(barChartData);
    const leftYLegendValues = getLabelsForYAxis(maxEnergyYValue, locale.toString(), chartLines);

    const { width, backgroundWidth } = getWidthOfBar(barChartData);

    return (
        <>
            <Stack
                data-testid={"bar-chart"}
                direction={"row"}
                width={"90%"}
                mx={"80px"}
                justifyContent="space-between"
                zIndex={20}
            >
                <>
                    {barChartData.map((data, i) => (
                        <Stack
                            direction="column"
                            justifyContent={"center"}
                            alignContent={"center"}
                            spacing="8px"
                            useFlexGap
                            key={i}
                            sx={{ width: backgroundWidth }}
                        >
                            <Box
                                height={"362px"}
                                marginTop={-2}
                                width={backgroundWidth}
                                position={"relative"}
                                padding={0}
                                sx={{
                                    "&:hover": {
                                        background: senecColors.neutralGray00,
                                    },
                                }}
                            >
                                <Box
                                    display={"flex"}
                                    justifyContent={"center"}
                                    alignItems={"flex-end"}
                                    zIndex={20}
                                    position={"absolute"}
                                    top={0}
                                    left={0}
                                    right={0}
                                    bottom={-7}
                                    padding={0}
                                >
                                    <Bar
                                        width={width}
                                        height={346}
                                        data={[data]}
                                        keys={["value"]}
                                        maxValue={maxEnergyYValue}
                                        padding={0.3}
                                        isInteractive={false}
                                        animate={false}
                                        label={""}
                                        layers={["bars"]}
                                        margin={{ top: 9, right: 0, bottom: 0, left: 0 }}
                                        colors={() => {
                                            if (timeframesToHighlight.includes(i)) {
                                                return theme.palette.primary.main;
                                            } else {
                                                return theme.palette.impulseOrange.main;
                                            }
                                        }}
                                    />
                                </Box>
                            </Box>
                            <Typography
                                variant="body1Responsive"
                                color={theme.palette.neutralGray50.main}
                                sx={{ textAlign: "center", marginLeft: "-100%", marginRight: "-100%" }}
                            >
                                {data.label ?? <span>&nbsp;</span>}
                            </Typography>
                        </Stack>
                    ))}
                </>
            </Stack>
            {showLeftAxis && !leftAxisConfig && (
                <YAxisLegend variant={"left"} chartType={"bar"} yLegendValues={leftYLegendValues} unit={DataSeriesUnit.KWH} />
            )}
            {leftAxisConfig && (
                <YAxisLegend
                    variant={"left"}
                    chartType={"bar"}
                    yLegendValues={maxEnergyYValue ? (leftAxisConfig.ticks ?? leftYLegendValues) : []}
                    unit={leftAxisConfig.unit}
                />
            )}
            <ChartGrid amountLines={chartLines} />
        </>
    );
};

export function getWidthOfBar(barChartData: BarChartDataItem[]) {
    const defaultWidth = 75;
    const numOfValues = barChartData.length || 1;
    const width = defaultWidth / (numOfValues / 3);
    const backgroundWidth = width * 2;

    return {
        width,
        backgroundWidth,
    };
}

export const calculateMaxEnergyYValue = (barDataList: BarChartDataItem[]) =>
    barDataList.length ? Math.max(1, ...barDataList.map(({ value }) => value)) : 0;
